import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AIAnalysisPanel from './AIAnalysisPanel';
import { IconBarChart, IconAlertTriangle } from './Icons';

const ClusteringScreen = ({ projectData, onComplete, onBack, onError, apiBaseUrl = 'http://localhost:5000' }) => {
  const [clusters, setClusters] = useState({});
  const [nClusters, setNClusters] = useState(5);
  const [loading, setLoading] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [textCount, setTextCount] = useState(0);
  const [projectId, setProjectId] = useState('');
  const [industry, setIndustry] = useState('products');
  const [advice, setAdvice] = useState(null);

  useEffect(() => {
    console.log('ClusteringScreen mounted with data:', projectData);
    
    // Если у нас уже есть полные данные проекта (включая кластеры), используем их напрямую
    if (projectData.project && projectData.project.clusters) {
      const project = projectData.project;
      setProjectId(project.id || projectData.project_id);
      setClusters(project.clusters || {});
      setNClusters(project.n_clusters || 5);
      setTextCount(project.text_count || 0);
      setIndustry(project.industry || 'products');
      console.log('Using project data from props');
    } 
    // Если есть только ID, загружаем данные с бэкенда
    else if (projectData.project_id) {
      setProjectId(projectData.project_id);
      fetchProjectData(projectData.project_id);
    } 
    // Запасной вариант для структуры с project напрямую
    else if (projectData.project) {
      const project = projectData.project;
      setProjectId(project.id);
      setClusters(project.clusters || {});
      setNClusters(project.n_clusters || 5);
      setTextCount(project.text_count || 0);
    }
  }, [projectData]);

  const fetchProjectData = async (projectId) => {
    setLoading(true);
    try {
      console.log('Fetching project from:', `${apiBaseUrl}/api/project/${projectId}`);
      const response = await axios.get(`${apiBaseUrl}/api/project/${projectId}`);
      
      if (response.data.success) {
        const project = response.data.project;
        setClusters(project.clusters || {});
        setNClusters(project.n_clusters || 5);
        setTextCount(project.text_count || 0);
        setIndustry(project.industry || 'products');
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      if (err.response && err.response.status === 404) {
        onError('Проект не найден на сервере. Возможно, сервер был перезагружен. Пожалуйста, загрузите файл заново.');
      } else {
        onError('Ошибка при загрузке данных проекта. Проверьте соединение с сервером.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Загружаем статистическую рекомендацию по числу кластеров
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/project/${projectId}/cluster-advice`);
        if (!cancelled && response.data && response.data.success) {
          setAdvice(response.data);
        }
      } catch (err) {
        console.warn('cluster-advice unavailable:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, apiBaseUrl]);

  const handleRecluster = async () => {
    if (!projectId) {
      onError('Проект не найден');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Reclustering at:', `${apiBaseUrl}/api/project/${projectId}/recluster`);
      const response = await axios.post(
        `${apiBaseUrl}/api/project/${projectId}/recluster`,
        { n_clusters: nClusters, industry: industry }
      );
      
      if (response.data.success) {
        setClusters(response.data.clusters || {});
        setNClusters(response.data.n_clusters || 5);
      }
    } catch (err) {
      console.error('Recluster error:', err);
      onError('Ошибка при перекластеризации');
    } finally {
      setLoading(false);
    }
  };

  const handleClusterClick = (clusterId) => {
    setSelectedCluster(selectedCluster === clusterId ? null : clusterId);
  };

  const handleContinue = () => {
    onComplete({
      project_id: projectId,
      clusters: clusters,
      n_clusters: Object.keys(clusters).length, // Используем фактическое количество полученных кластеров
      text_count: textCount
    });
  };

  // Функция для безопасного доступа к данным кластера
  const getClusterData = (cluster) => {
    if (!cluster) return {
      size: 0,
      percentage: 0,
      suggested_name: 'Кластер',
      top_ngrams: [],
      examples: []
    };
    
    return {
      size: cluster.size || 0,
      percentage: cluster.percentage || 0,
      suggested_name: cluster.suggested_name || 'Кластер',
      top_ngrams: cluster.top_ngrams || [],
      examples: cluster.examples || []
    };
  };

  // Преобразование кластеров в массив для отображения
  const clustersArray = Object.keys(clusters).length > 0 
    ? Object.entries(clusters) 
    : [];

  return (
    <div className="clustering-screen">
      <div className="clustering-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>2. Автоматическая кластеризация</h2>
          <div style={{ 
            padding: '5px 15px', 
            backgroundColor: 'var(--panel-2)', 
            borderRadius: '20px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: 'var(--muted)',
            border: '1px solid #d1d9e6'
          }}>
            Отрасль: {industry === 'products' ? 'Продукты питания' : industry === 'non_food' ? 'Товары' : industry === 'banks' ? 'Банки и финансы' : industry === 'organoleptic' ? 'Органолептика' : industry === 'generic' ? 'Ритейл и маркетплейсы' : 'Услуги'}
          </div>
        </div>
        <div className="controls">
          <div className="cluster-control">
            <label>Количество кластеров:</label>
            <input
              type="number"
              min="2"
              max="20"
              value={nClusters}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 2 && value <= 20) {
                  setNClusters(value);
                }
              }}
              style={{ width: '60px', margin: '0 10px', padding: '5px',
                border: (advice && nClusters < advice.min_reasonable) ? '2px solid #ff9800' : '1px solid var(--border)',
                borderRadius: '4px' }}
            />
            <button 
              onClick={handleRecluster} 
              disabled={loading || !projectId}
              className="recluster-button"
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Обработка...' : 'Перекластеризовать'}
            </button>
          </div>
          {advice && (
            <div style={{ marginTop: '10px', fontSize: '13px' }}>
              <div style={{
                display: 'inline-block', padding: '6px 12px', borderRadius: '8px',
                backgroundColor: 'rgba(100, 181, 246, 0.12)', border: '1px solid rgba(100, 181, 246, 0.4)', color: '#9ecbff'
              }}>
                <IconBarChart size={14} style={{ marginRight: '6px', marginTop: '-2px' }} />Рекомендуется <b>{advice.optimal}</b> кластеров
                {advice.min_reasonable !== advice.max_reasonable && (
                  <> (разумный диапазон <b>{advice.min_reasonable}–{advice.max_reasonable}</b>)</>
                )}
              </div>
              {nClusters < advice.min_reasonable && (
                <div style={{
                  marginTop: '8px', padding: '8px 12px', borderRadius: '8px',
                  backgroundColor: 'rgba(255, 152, 0, 0.12)', border: '1px solid rgba(255, 152, 0, 0.45)', color: '#ffc46b', maxWidth: '640px'
                }}>
                  <IconAlertTriangle size={14} style={{ marginRight: '6px', marginTop: '-2px' }} />Выбрано {nClusters} — это меньше статистически обоснованного минимума ({advice.min_reasonable}).
                  Разные темы могут слиться в один кластер. Решение за вами, но рекомендуем не ниже {advice.min_reasonable}.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid var(--border)',
            borderTop: '5px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '20px', fontSize: '16px' }}>Выполняется кластеризация...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {!loading && clustersArray.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'var(--panel-2)',
          borderRadius: '8px',
          margin: '20px 0',
          border: '1px dashed var(--border)'
        }}>
          <p style={{ fontSize: '18px', marginBottom: '20px', color: 'var(--muted)' }}>
            {projectId ? 'Кластеры не найдены или проект пустой' : 'Проект не создан'}
          </p>
          {projectId ? (
            <button 
              onClick={handleRecluster}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Начать кластеризацию
            </button>
          ) : (
            <p style={{ color: '#dc3545' }}>
              Вернитесь назад и создайте проект
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="clusters-container">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px',
              marginTop: '20px'
            }}>
              {clustersArray.map(([clusterId, clusterData]) => {
                const cluster = getClusterData(clusterData);
                return (
                  <div
                    key={clusterId}
                    onClick={() => handleClusterClick(parseInt(clusterId))}
                    style={{
                      border: selectedCluster === parseInt(clusterId) ? '1px solid #007bff' : '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '20px',
                      backgroundColor: selectedCluster === parseInt(clusterId) ? 'rgba(0, 123, 255, 0.15)' : 'var(--panel)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '15px'
                    }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: 'var(--muted)'
                      }}>
                        Кластер {parseInt(clusterId) + 1}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--muted)',
                        backgroundColor: 'var(--border)',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}>
                        {cluster.size} текстов ({cluster.percentage}%)
                      </div>
                    </div>
                    
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      marginBottom: '15px',
                      padding: '8px',
                      backgroundColor: 'rgba(95, 212, 232, 0.12)',
                      border: '1px solid rgba(95, 212, 232, 0.35)',
                      borderRadius: '4px'
                    }}>
                      {cluster.suggested_name}
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--muted)',
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        Ключевые слова:
                      </div>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        {cluster.top_ngrams.slice(0, 5).map((ngram, idx) => (
                          <span 
                            key={idx}
                            style={{
                              backgroundColor: 'rgba(0, 123, 255, 0.18)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              color: '#64b5f6',
                              border: '1px solid rgba(100, 181, 246, 0.35)'
                            }}
                          >
                            {ngram.ngram} ({ngram.count})
                          </span>
                        ))}
                        {cluster.top_ngrams.length === 0 && (
                          <span style={{ color: 'var(--muted)', fontSize: '14px', fontStyle: 'italic' }}>
                            Нет ключевых слов
                          </span>
                        )}
                      </div>
                    </div>

                    {selectedCluster === parseInt(clusterId) && (
                      <div style={{
                        marginTop: '15px',
                        paddingTop: '15px',
                        borderTop: '1px solid var(--border)'
                      }}>
                        <h4 style={{ 
                          marginBottom: '10px', 
                          fontSize: '14px', 
                          color: 'var(--text)',
                          fontWeight: '600'
                        }}>
                          Примеры текстов:
                        </h4>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {cluster.examples.map((example, idx) => (
                            <li 
                              key={idx}
                              style={{
                                padding: '8px 0',
                                borderBottom: idx < cluster.examples.length - 1 ? '1px solid var(--border)' : 'none',
                                fontSize: '13px',
                                color: 'var(--muted)',
                                lineHeight: '1.4'
                              }}
                            >
                              {example}
                            </li>
                          ))}
                          {cluster.examples.length === 0 && (
                            <li style={{ 
                              color: 'var(--muted)', 
                              fontSize: '14px',
                              fontStyle: 'italic',
                              padding: '10px 0'
                            }}>
                              Нет примеров
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            margin: '30px 0',
            padding: '20px',
            backgroundColor: 'var(--panel-2)',
            borderRadius: '8px',
            border: '1px solid var(--border)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#64b5f6'
              }}>
                {clustersArray.length}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--muted)', marginTop: '5px' }}>
                кластеров
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#28a745'
              }}>
                {textCount}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--muted)', marginTop: '5px' }}>
                текстов
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI-анализ кластеров */}
      {clustersArray.length > 0 && (
        <AIAnalysisPanel
          projectId={projectId}
          clusters={clusters}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid var(--border)'
      }}>
        <button 
          onClick={onBack}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          ← Назад
        </button>
        <button 
          onClick={handleContinue}
          disabled={clustersArray.length === 0}
          style={{
            padding: '10px 20px',
            backgroundColor: clustersArray.length === 0 ? 'var(--border)' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: clustersArray.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: clustersArray.length === 0 ? 0.6 : 1,
            fontWeight: 'bold'
          }}
        >
          Продолжить редактирование →
        </button>
      </div>
    </div>
  );
};

export default ClusteringScreen;
